/**
 * PdfPageExtractorModal - Main modal for extracting pages from a PDF
 */
import {
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ScissorsIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Input } from '@/components/ui/input';
import { useGetCollectionTreeQuery } from '@/services/api/collectionService';

import { CollectionPicker } from './CollectionPicker';
import { PdfPageNavigator } from './PdfPageNavigator';
import type { PdfPageExtractorModalProps } from './types';
import { usePdfPageExtraction } from './usePdfPageExtraction';
import { generateExtractedFilename } from './utils';

export const PdfPageExtractorModal = ({
  isOpen,
  onClose,
  matterId,
  sourceBlobId,
  sourceUrl,
  sourceName,
  defaultCollectionId,
  onExtractComplete,
  context = 'general',
  zIndex,
  nested = false,
}: PdfPageExtractorModalProps) => {
  // State
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [totalPages, setTotalPages] = useState(0);
  const [filename, setFilename] = useState('');
  const [selectedCollectionId, setSelectedCollectionId] = useState<
    string | null
  >(defaultCollectionId || null);

  // Get root collection as default if none specified
  const { data: collectionTree } = useGetCollectionTreeQuery(matterId, {
    skip: !matterId || !!defaultCollectionId,
  });

  // Set default collection on mount
  useEffect(() => {
    if (!selectedCollectionId && collectionTree) {
      setSelectedCollectionId(collectionTree.id);
    }
  }, [collectionTree, selectedCollectionId]);

  // Extraction hook
  const {
    progress,
    extractAndUpload,
    reset,
    isExtracting,
    isComplete,
    hasError,
  } = usePdfPageExtraction({
    matterId,
    onSuccess: (result) => {
      toast.success(`"${result.filename}" saved to ${result.collectionName}`);
      onExtractComplete?.(result.blobId);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to extract pages');
    },
  });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedPages(new Set());
      setTotalPages(0);
      setFilename('');
      reset();
    }
  }, [isOpen, reset]);

  // Handle PDF load
  const handlePdfLoad = useCallback((pageCount: number) => {
    setTotalPages(pageCount);
  }, []);

  // Update filename when selection changes
  useEffect(() => {
    if (selectedPages.size > 0 && !filename) {
      setFilename(generateExtractedFilename(sourceName, selectedPages.size));
    }
  }, [selectedPages.size, sourceName, filename]);

  // Ensure filename has .pdf extension
  const ensurePdfExtension = (name: string): string => {
    if (!name) return name;
    return name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
  };

  // Handle extract action
  const handleExtract = useCallback(async () => {
    if (selectedPages.size === 0 || !selectedCollectionId) return;

    await extractAndUpload({
      sourceBlobId,
      sourceUrl,
      sourceName,
      pageNumbers: Array.from(selectedPages).sort((a, b) => a - b),
      collectionId: selectedCollectionId,
      filename: filename ? ensurePdfExtension(filename) : undefined,
    });
  }, [
    selectedPages,
    selectedCollectionId,
    sourceBlobId,
    sourceUrl,
    sourceName,
    filename,
    extractAndUpload,
  ]);

  // Handle close
  const handleClose = useCallback(() => {
    if (!isExtracting) {
      onClose();
    }
  }, [isExtracting, onClose]);

  // Get progress percentage for display
  const progressPercent = useMemo(() => {
    switch (progress.status) {
      case 'loading-pdf':
        return 10;
      case 'extracting':
        return 40;
      case 'uploading':
        return 70 + progress.progress * 0.3;
      case 'complete':
        return 100;
      default:
        return 0;
    }
  }, [progress]);

  // Get progress message
  const progressMessage = useMemo(() => {
    switch (progress.status) {
      case 'loading-pdf':
        return 'Loading source document...';
      case 'extracting':
        return 'Extracting selected pages...';
      case 'uploading':
        return 'Uploading extracted document...';
      case 'complete':
        return 'Extraction complete!';
      default:
        return '';
    }
  }, [progress.status]);

  // Context-aware success actions
  const renderSuccessActions = () => {
    const actions: React.ReactNode[] = [];

    if (context === 'signed-auth') {
      actions.push(
        <Button
          key="use-auth"
          color="indigo"
          onClick={() => {
            handleClose();
          }}
        >
          Use as Signed Authorization
        </Button>,
      );
    }

    actions.push(
      <Button key="done" color="white" onClick={handleClose}>
        Done
      </Button>,
    );

    return actions;
  };

  // Render success state
  const renderSuccessState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-6">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
        <CheckCircleIcon className="w-10 h-10 text-green-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Extraction Complete
      </h3>
      <p className="text-sm text-gray-500 text-center mb-6">
        Your document has been saved successfully.
      </p>
      <div className="flex items-center gap-3">{renderSuccessActions()}</div>
    </div>
  );

  // Render error state
  const renderErrorState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-6">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
        <ExclamationTriangleIcon className="w-10 h-10 text-red-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Extraction Failed
      </h3>
      <p className="text-sm text-gray-500 text-center mb-6">
        {progress.error || 'An error occurred during extraction.'}
      </p>
      <div className="flex items-center gap-3">
        <Button
          color="indigo"
          onClick={() => {
            reset();
          }}
        >
          Try Again
        </Button>
        <Button color="white" onClick={handleClose}>
          Cancel
        </Button>
      </div>
    </div>
  );

  // Render progress state
  const renderProgressState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-6">
      <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
        <DocumentTextIcon className="w-10 h-10 text-indigo-600 animate-pulse" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {progressMessage}
      </h3>
      <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
        <div
          className="h-full bg-indigo-500 transition-all duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <p className="text-sm text-gray-400">{Math.round(progressPercent)}%</p>
    </div>
  );

  // Render selection state
  const renderSelectionState = () => (
    <div className="flex flex-col h-[70vh]">
      {/* Page navigator */}
      <div className="flex-1 px-6 pt-2 pb-4 min-h-0">
        <PdfPageNavigator
          pdfUrl={sourceUrl}
          selectedPages={selectedPages}
          onSelectionChange={setSelectedPages}
          onPdfLoad={handlePdfLoad}
        />
      </div>

      {/* Footer with inputs */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-4">
        {/* Filename and collection */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <span className="block text-xs font-medium text-gray-700 mb-1.5">
              Filename
            </span>
            <Input
              type="text"
              placeholder="Enter filename..."
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              className="h-9"
              aria-label="Filename for extracted PDF"
            />
          </div>
          <div>
            <span className="block text-xs font-medium text-gray-700 mb-1.5">
              Save to
            </span>
            <CollectionPicker
              matterId={matterId}
              selectedCollectionId={selectedCollectionId}
              onSelectionChange={setSelectedCollectionId}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button color="white" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color="indigo"
            onClick={handleExtract}
            disabled={
              selectedPages.size === 0 ||
              !selectedCollectionId ||
              !filename.trim()
            }
          >
            <ScissorsIcon className="w-4 h-4 mr-1.5" />
            Extract {selectedPages.size > 0 && `(${selectedPages.size})`}{' '}
            Page{selectedPages.size !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </div>
  );

  // Get the appropriate content based on state
  const getDialogContent = () => {
    if (isComplete) return renderSuccessState();
    if (hasError) return renderErrorState();
    if (isExtracting) return renderProgressState();
    return renderSelectionState();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} size="5xl" zIndex={zIndex} nested={nested}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <ScissorsIcon className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Extract Pages
            </DialogTitle>
            <p className="text-sm text-gray-500 truncate max-w-md">
              Source: {sourceName}{' '}
              {totalPages > 0 && (
                <span className="text-gray-400">({totalPages} pages)</span>
              )}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClose}
          disabled={isExtracting}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      <DialogBody className="p-0">{getDialogContent()}</DialogBody>
    </Dialog>
  );
};

export default PdfPageExtractorModal;
